import dayjs from 'dayjs';
import React, { useState, useEffect, useRef } from 'react';
import { CopyOutlined, KeyOutlined, SyncOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Space, Statistic, Typography, message } from 'antd';

import http from '../../http';

const { Title, Text } = Typography;
const { Countdown } = Statistic;

const POLL_INTERVAL_MS = 3000;
const LS_KEY = 'otp_state'; // stored: { id, otp, expiresAt }

const saveToStorage = (id, otp, expiresAt) => {
    localStorage.setItem(LS_KEY, JSON.stringify({ id, otp, expiresAt }));
};

const clearStorage = () => {
    localStorage.removeItem(LS_KEY);
};

const loadFromStorage = () => {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const { id, otp, expiresAt } = JSON.parse(raw);
        if (!id || !otp || !expiresAt) return null;
        if (dayjs().valueOf() >= expiresAt) {
            clearStorage();
            return null;
        }
        return { id, otp, expiresAt };
    } catch {
        clearStorage();
        return null;
    }
};

const OtpSettings = () => {
    const [session, setSession] = useState(null); // { id, otp, expiresAt } | null
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);

    const stopPoll = () => {
        clearInterval(pollRef.current);
        pollRef.current = null;
    };

    const endSession = () => {
        clearStorage();
        stopPoll();
        setSession(null);
    };

    const checkStatus = async (id) => {
        try {
            const resp = await http.get(`/api/v1/auth/otp/status/${id}/`);
            const { is_valid, is_used } = resp.data;
            if (!is_valid || is_used) endSession();
        } catch {
            endSession();
        }
    };

    const startPoll = (id) => {
        stopPoll();
        pollRef.current = setInterval(() => checkStatus(id), POLL_INTERVAL_MS);
    };

    // Restore from localStorage on mount, verify against server immediately
    useEffect(() => {
        const stored = loadFromStorage();
        if (stored) {
            setSession(stored);
            checkStatus(stored.id);
            startPoll(stored.id);
        }
        return () => stopPoll();
    }, []);

    const generate = async () => {
        setLoading(true);
        try {
            const resp = await http.post('/api/v1/auth/otp/generate/');
            const { id, otp, expires_at } = resp.data;
            const expiresAt = dayjs(expires_at).valueOf();
            setSession({ id, otp, expiresAt });
            saveToStorage(id, otp, expiresAt);
            startPoll(id);
        } catch {
            message.error('Failed to generate OTP.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%', alignItems: 'center', display: 'flex' }}>
            <Title level={3}>One Time Password</Title>
            <Text type="secondary">
                Generate a 6-digit OTP. Valid for 2 minutes.
            </Text>

            {session && (
                <Card style={{ textAlign: 'center', minWidth: 560, padding: '16px 0' }}>
                    <Title
                        level={1}
                        style={{ letterSpacing: '0.8rem', margin: 0, fontFamily: 'monospace', fontSize: '3.5rem' }}
                    >
                        {session.otp}
                    </Title>
                    <Countdown
                        title="Expires in"
                        value={session.expiresAt}
                        onFinish={endSession}
                        format="mm:ss"
                        style={{ marginTop: 8 }}
                    />
                    <Button
                        icon={<CopyOutlined />}
                        type="link"
                        size="small"
                        onClick={() => {
                            navigator.clipboard.writeText(session.otp).then(
                                () => message.success('OTP copied!'),
                                () => message.error('Copy failed'),
                            )
                        }}
                        style={{ marginTop: 4 }}
                    >
                        Copy
                    </Button>
                </Card>
            )}

            {!session && (
                <Button
                    type="primary"
                    icon={loading ? <SyncOutlined spin /> : <KeyOutlined />}
                    onClick={generate}
                    loading={loading}
                >
                    Generate OTP
                </Button>
            )}
        </Space>
    );
};

export default OtpSettings;
